import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';

/**
 * AutoSwipePanels
 * - Swipe left/right to change panel
 * - Auto-advances every `interval` ms
 * - Keeps same outer API shape as your old usage: just wrap children
 */
export default function AutoSwipePanels({
  children,
  interval = 15000,
  initialIndex = 0,
  onIndexChange,
  style,
  className,
}) {
  const items = React.Children.toArray(children);
  const [index, setIndex] = useState(initialIndex);
  const pausedRef = useRef(false);

  // Keep parent tabs or state in sync if needed
  useEffect(() => {
    if (onIndexChange) onIndexChange(index);
  }, [index, onIndexChange]);

  // Swipe handlers
  const handlers = useSwipeable({
    onSwipedLeft: () => setIndex((i) => Math.min(i + 1, items.length - 1)),
    onSwipedRight: () => setIndex((i) => Math.max(i - 1, 0)),
    trackMouse: true, // allow drag on desktop
  });

  // Auto-play
  useEffect(() => {
    if (items.length <= 1) return;
    const id = setInterval(() => {
      if (pausedRef.current) return;
      setIndex((i) => (i + 1) % items.length);
    }, interval);
    return () => clearInterval(id);
  }, [interval, items.length]);

  const trackStyle = useMemo(
    () => ({
      display: 'flex',
      width: `${items.length * 100}%`,
      transform: `translateX(-${index * (100 / items.length)}%)`,
      transition: 'transform 260ms ease',
      height: '100%',
    }),
    [index, items.length]
  );

  const wrapperStyle = {
    overflow: 'hidden',
    touchAction: 'pan-y',
    height: '100%',
    ...style,
  };

  return (
    <div
      {...handlers}
      className={className}
      style={wrapperStyle}
      onMouseEnter={() => (pausedRef.current = true)}
      onMouseLeave={() => (pausedRef.current = false)}
      onTouchStart={() => (pausedRef.current = true)}
      onTouchEnd={() => (pausedRef.current = false)}
    >
      <div style={trackStyle}>
        {items.map((node, idx) => (
          <div
            key={idx}
            style={{ width: `${100 / items.length}%`, flexShrink: 0, height: '100%' }}
          >
            {node}
          </div>
        ))}
      </div>
    </div>
  );
}